// pages/sections/BpprReviewSection.js
// BPPR Review — Dev/admin-only section (manager role). Shows the BPPR (Business
// Personal Property Return) jobs ASSIGNED TO the signed-in user for the active
// period (from /api/diBpprReview/{period} — Dan's population, mock for now).
//
// Per row: Accept (placeholder) and Reject. Reject opens a picker of the
// client's CAS/Tax/Audit managers and emails the chosen one (POST action
// 'reject'). Data comes with each row's serviceManagers for that picker.
//
// SectionPage renders this for section_type === 'bppr-review' and provides the
// TopBar + "Dashboard" back button, so we only render the body.

import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import { useTourDemo } from '../../lib/tourDemo';
import { bpprDemoData } from './bpprReviewDemoData';
import LastRefreshed from '../../components/LastRefreshed';
import Modal from '../../components/Modal';
import ToggleSwitch from './ToggleSwitch';
import { sortDoneLast } from './reviewUtil';
import './ReviewTable.css';
import './BpprReviewSection.css';

const SERVICE_ROLE_LABEL = { cas: 'CAS', tax: 'Tax', assurance: 'Audit' };

// The three service-line managers shown in the Managers column, in order.
const MANAGER_ROLES = [
  { key: 'cas',       label: 'CAS' },
  { key: 'tax',       label: 'Tax' },
  { key: 'assurance', label: 'Audit' },
];

// Two-letter initials from a display name (or email) for the avatar chip.
function initials(nameOrEmail) {
  const s = (nameOrEmail || '').split('@')[0] || '';
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase() || '?';
}

// A row is finished when nothing is left to do on it: signed off, closed & won't
// roll, or acknowledged. Drives the Active / Completed split.
const isDone = (r) => !!(r.closed || r.reviewed);

// "<text> — <who> on <date>" for a chip tooltip, skipping the parts we don't have.
function byLine(text, who, at) {
  const when = at ? new Date(at) : null;
  const stamp = when && !isNaN(when.getTime()) ? when.toLocaleDateString() : null;
  if (!who && !stamp) return text;
  return `${text}\n${[who, stamp && `on ${stamp}`].filter(Boolean).join(' ')}`;
}

// Outcome chips for a finished row. A job can be BOTH signed off and flagged not
// to roll, so this returns a list rather than one label: the single
// "Reviewed — Closed" badge it replaces hid which of the three outcomes actually
// happened, and read like Close & Don't Roll even when the job still rolls.
function outcomeTags(row) {
  const tags = [];
  if (row.reviewed && row.assigned) {
    tags.push({
      key: 'reviewed', label: 'Reviewed', cls: 'bppr-otag--reviewed',
      title: byLine('Accepted and signed off. This job still rolls forward next year.', row.reviewedBy, row.reviewedAt),
    });
  }
  if (row.reviewed && !row.assigned) {
    tags.push({
      key: 'ack', label: 'Acknowledged', cls: 'bppr-otag--ack',
      title: byLine("Accounted for without taking ownership — this job wasn't assigned to you.", row.reviewedBy, row.reviewedAt),
    });
  }
  if (row.closed) {
    tags.push({
      key: 'noroll', label: "Closed — Won't Roll", cls: 'bppr-otag--noroll',
      title: byLine('Flagged not to roll forward next year. Recorded now, applied in Practice Engine later.', row.closedBy, row.closedAt),
    });
  }
  return tags;
}

// The client's CAS/Tax/Audit managers a rejected job can be handed to — minus
// the current user (you can't reassign a job to yourself in this mode).
function handoffOptions(row, myEmail) {
  const sm = (row && row.serviceManagers) || {};
  const names = (row && row.serviceManagerNames) || {};
  const me = (myEmail || '').toLowerCase();
  return ['cas', 'tax', 'assurance']
    .map(k => ({ key: k, label: SERVICE_ROLE_LABEL[k], email: sm[k], name: names[k] || sm[k] }))
    .filter(o => o.email && o.email.toLowerCase() !== me);
}

export default function BpprReviewSection() {
  const { email } = useAuth();
  const { period } = usePeriod();
  // Spotlight tour: render curated demo jobs instead of the live feed, and
  // no-op every write so a click during/after the walkthrough can't hit prod.
  const demo = useTourDemo();

  const [rejectRow,  setRejectRow]  = useState(null);        // row being rejected (or null)
  const [rejectMode, setRejectMode] = useState('managers');  // 'managers' | 'wip' | 'any'
  const [rejectTo,   setRejectTo]   = useState('');          // chosen recipient email
  const [rejectNote, setRejectNote] = useState('');          // optional note (goes in the email)
  const [closeRow,  setCloseRow]  = useState(null);   // row pending close-confirm (or null)
  const [helpOpen,  setHelpOpen]  = useState(false);
  // Admin-only firm-wide oversight, two independent toggles (kept separate so
  // neither list gets long). `showClosed` → every FINISHED job (?closed=1) for
  // Undo close/review; `showInProgress` → every ACCEPTED-not-finished job
  // (?inprogress=1) for Undo acceptance. Both off by default.
  const [showClosed, setShowClosed] = useState(false);
  const [showInProgress, setShowInProgress] = useState(false);
  // Which queue the grid is showing. 'active' = still needs work; 'completed' =
  // finished (reviewed & closed / closed-no-roll / acknowledged). The backend
  // returns both for the caller's own jobs; this only chooses what's rendered.
  const [tab, setTab] = useState('active');

  // Jobs the caller hid this session by closing them (manager view). Kept out of
  // poll refreshes so a mock/example close stays hidden until a page reload (which
  // remounts and clears this). Real closes are also filtered out server-side.
  const hiddenRef = useRef(new Set());

  // Optimistic saves: ordered per job, never disable the controls, and a refetch can't overwrite an
  // edit that is still on the wire.
  const mutations = useRowMutations(r => r.jobIdx);
  const reconcileRows = mutations.reconcile;
  const reconcile = useCallback((prev, next, opts) => {
    if (!next) return next;
    const merged = reconcileRows(prev, next, opts);
    if (!hiddenRef.current.size) return merged;
    return { ...merged, rows: (merged.rows || []).filter(r => !hiddenRef.current.has(String(r.jobIdx))) };
  }, [reconcileRows]);

  const fetcher = useCallback(async () => {
    if (demo) return bpprDemoData();   // tour: curated jobs, no network
    if (!period) return null;
    const params = [];                              // admin firm-wide oversight flags
    if (showClosed)     params.push('closed=1');
    if (showInProgress) params.push('inprogress=1');
    const qs = params.length ? `?${params.join('&')}` : '';
    const res = await fetch(`/api/diBpprReview/${period}${qs}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period, demo, showClosed, showInProgress]);

  // The app heartbeat (every 15 s) refetches when another user's BPPR action or a
  // rebuilt PE snapshot lands; data swaps in place with no spinner. `demo` is in
  // deps so entering/leaving the tour re-fetches immediately (sample clients in,
  // real data back out). During the tour the heartbeat is OFF — there's no live
  // data to refresh, and a background refetch racing the demo render is what made
  // the grid flicker between the sample rows and empty.
  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch } =
    useSectionData(fetcher, { deps: [period, email, demo, showClosed, showInProgress], reconcile,
      heartbeat: demo ? null : { sections: ['bppr-review'], period } });

  // Curated sample jobs for the spotlight tour. A stable snapshot rendered the
  // instant the tour turns on, so the grid never shows loading/empty/real while
  // the hook's demo fetch is still in flight. Once that fetch lands (source
  // 'demo') we prefer it, so optimistic clicks during the walkthrough still show.
  const demoSeed = useMemo(() => bpprDemoData(), []);

  // Optimistic single-row patch. (Snapshots for revert come from the RENDERED row, never from inside
  // the updater — React may run the updater later.)
  function patchRow(jobIdx, updater) {
    setData(d => {
      if (!d) return d;
      return {
        ...d,
        rows: (d.rows || []).map(r => (String(r.jobIdx) === String(jobIdx) ? updater(r) : r)),
      };
    });
  }

  // Remove a row from view and keep it out of subsequent polls (this session).
  function hideRow(jobIdx) {
    hiddenRef.current.add(String(jobIdx));
    setData(d => (d ? { ...d, rows: (d.rows || []).filter(r => String(r.jobIdx) !== String(jobIdx)) } : d));
  }

  // Undo hideRow after a refused save: the row goes back where it was.
  function unhideRow(snap, index) {
    hiddenRef.current.delete(String(snap.jobIdx));
    setData(d => {
      if (!d) return d;
      const rows = d.rows || [];
      if (rows.some(r => String(r.jobIdx) === String(snap.jobIdx))) {
        return { ...d, rows: rows.map(r => (String(r.jobIdx) === String(snap.jobIdx) ? snap : r)) };
      }
      const at = Math.min(Math.max(index, 0), rows.length);
      return { ...d, rows: [...rows.slice(0, at), snap, ...rows.slice(at)] };
    });
  }

  const renderedRow = jobIdx => (data?.rows || []).find(r => String(r.jobIdx) === String(jobIdx));

  // POST one BPPR action; throws Error(message) when the server refuses.
  async function postBppr(body) {
    const res = await fetch(`/api/diBpprReview/${period}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j.ok === false) throw new Error(j.error || `HTTP ${res.status}`);
    return j;
  }

  // The one optimistic path for every BPPR write: apply `patch` (and/or hide the row) at once, send the
  // request queued per job, and on a refusal put the row back and show the server's message.
  //   body     — the POST payload (the request)
  //   patch    — row => row' applied immediately
  //   hide     — also remove the row from view immediately (firm-wide rows an action finishes)
  //   ok / fail — success toast text / failure toast prefix; demo — tour toast (no network)
  // Resolves true when the server accepted it, false when it refused (already reverted + toasted).
  async function optimistic(row, { body, patch, hide = false, ok, fail, demoMsg, refetch = false }) {
    const key = row.jobIdx;
    const snap = renderedRow(key) || row;
    const index = (data?.rows || []).findIndex(r => String(r.jobIdx) === String(key));
    if (patch) patchRow(key, patch);
    if (hide) hideRow(key);
    if (demo) { toast.success(demoMsg); return true; }
    const restore = () => {
      if (hide) unhideRow(snap, index);
      else patchRow(key, () => snap);
    };
    try {
      await mutations.run(key, () => postBppr(body), { revert: restore, refetch: silentRefetch });
      toast.success(ok);
      if (refetch) silentRefetch();
      return true;
    } catch (e) {
      if (hide) { hiddenRef.current.delete(String(key)); silentRefetch(); }   // the row must not stay hidden
      toast.error(`${fail}: ${e.message}`, { autoClose: 10000 });
      return false;
    }
  }

  // Acknowledge an UNASSIGNED job → marks it reviewed (persisted to audit_log).
  function acknowledge(row) {
    return optimistic(row, {
      body: { action: 'acknowledge', jobIdx: row.jobIdx },
      patch: r => ({ ...r, reviewed: true, reviewedBy: 'You' }),
      ok: 'Acknowledged — marked reviewed.', fail: "Couldn't acknowledge",
      demoMsg: 'Acknowledged — marked reviewed. (demo)',
    });
  }

  // Accept a job assigned to me → takes ownership and enables the Reviewed
  // toggle; Reject drops away. Does NOT mark reviewed (that's a separate toggle).
  function accept(row) {
    return optimistic(row, {
      body: { action: 'accept', jobIdx: row.jobIdx },
      patch: r => ({ ...r, accepted: true }),
      ok: 'Accepted.', fail: "Couldn't accept",
      demoMsg: 'Accepted. (demo)',
    });
  }

  // The Mark Reviewed toggle → mark on, clear off.
  function toggleReviewed(row, checked) {
    if (checked) markReviewed(row);
    else         unmarkReviewed(row);
  }

  // Mark a job assigned to me reviewed → signs off AND closes it (it still rolls
  // next year; that's what Close & Don't Roll is for). The row leaves the Active
  // tab and lands under Completed, where its owner can undo — nothing vanishes.
  function markReviewed(row) {
    return optimistic(row, {
      body: { action: 'markReviewed', jobIdx: row.jobIdx },
      patch: r => ({ ...r, reviewed: true, reviewedBy: 'You' }),
      ok: "Reviewed & closed — moved to Completed. It'll still roll next year.", fail: "Couldn't mark reviewed",
      demoMsg: 'Reviewed & closed — moved to Completed. (demo)',
    });
  }

  // Toggle a review off → clears the signoff (persisted). An own row moves back
  // to the Active tab; firm-wide rows (not the admin's own) drop out of the
  // closed-jobs list once reopened — they're no longer finished.
  function unmarkReviewed(row) {
    return optimistic(row, {
      body: { action: 'unmarkReviewed', jobIdx: row.jobIdx },
      patch: r => ({ ...r, reviewed: false, reviewedBy: null }),
      hide: !row.mine,
      ok: row.mine ? 'Reviewed cleared — back in Active.' : 'Reviewed cleared.', fail: "Couldn't clear review",
      demoMsg: 'Reviewed cleared. (demo)',
    });
  }

  // Recipient of a handed-off job accepts it → it becomes theirs (reviewed).
  function acceptHandoff(row) {
    return optimistic(row, {
      body: { action: 'acceptHandoff', jobIdx: row.jobIdx },
      patch: r => ({ ...r, pendingForMe: false, assigned: true, accepted: true }),
      ok: "Accepted — it's yours now.", fail: "Couldn't accept",
      demoMsg: "Accepted — it's yours now. (demo)",
      refetch: true,
    });
  }

  // Sender pulls a not-yet-accepted handoff back into their own queue. Silent by
  // design (no email) — the recipient never took it on. The row becomes actionable
  // again right here, and it re-enters this user's counts on Home / Steering.
  function pullBack(row) {
    const who = row.handoffTo || 'them';
    return optimistic(row, {
      body: { action: 'pullBackHandoff', jobIdx: row.jobIdx },
      patch: r => ({ ...r, handoffPending: false, handoffTo: null, handoffToEmail: null }),
      ok: `Pulled back from ${who} — it's yours again.`, fail: "Couldn't pull it back",
      demoMsg: `Pulled back from ${who}. (demo)`,
    });
  }

  // Recipient declines → the job bounces back to the manager who handed it off.
  function declineHandoff(row) {
    return optimistic(row, {
      body: { action: 'declineHandoff', jobIdx: row.jobIdx },
      hide: true,
      ok: 'Declined — sent back to the original manager.', fail: "Couldn't decline",
      demoMsg: 'Declined — sent back to the original manager. (demo)',
      refetch: true,
    });
  }

  function openClose(row)  { setCloseRow(row); }
  function cancelClose()   { setCloseRow(null); }

  // Close & Don't Roll — the backend emails a confirmation and only records the
  // close if the send succeeds. Optimistic: the modal closes and the row moves to the
  // Completed tab (with an Undo) at once; if the server refuses (e.g. the email can't
  // be sent) the row goes back to Active and the server's message is shown.
  function confirmClose() {
    if (!closeRow) return;
    const row = closeRow;
    setCloseRow(null);
    return optimistic(row, {
      body: { action: 'closeNoRoll', jobIdx: row.jobIdx },
      patch: r => ({ ...r, closed: true, closedBy: 'You' }),
      ok: "Closed — won't roll next year. Moved to Completed; confirmation emailed.", fail: "Couldn't close",
      demoMsg: "Closed — won't roll next year. Moved to Completed. (demo)",
    });
  }

  // Reopen a closed job — its owner (from the Completed tab) or an admin. An own
  // row moves back to Active; a firm-wide row (not the admin's own) drops out of
  // the closed-jobs list once reopened, since it's no longer finished.
  function undoClose(row) {
    return optimistic(row, {
      body: { action: 'undoClose', jobIdx: row.jobIdx },
      patch: r => ({ ...r, closed: false, closedBy: null }),
      hide: !row.mine,
      ok: row.mine ? 'Reopened — back in Active.' : 'Reopened.', fail: "Couldn't undo",
      demoMsg: 'Reopened. (demo)',
    });
  }

  // Admin-only (or owner): undo an acceptance → back to the un-accepted state.
  // For a firm-wide row (not the admin's own), drop it from the in-progress list
  // once undone — it's no longer accepted.
  function unaccept(row) {
    return optimistic(row, {
      body: { action: 'unaccept', jobIdx: row.jobIdx },
      patch: r => ({ ...r, accepted: false }),
      hide: !row.mine,
      ok: 'Acceptance undone.', fail: "Couldn't undo acceptance",
      demoMsg: 'Acceptance undone. (demo)',
    });
  }

  function openReject(row) { setRejectRow(row); setRejectMode('managers'); setRejectTo(''); setRejectNote(''); }
  function closeReject()   { setRejectRow(null); setRejectTo(''); setRejectNote(''); }
  function selectMode(m)   { setRejectMode(m); setRejectTo(''); }   // switching pools clears the pick

  // Optimistic: the modal closes and the row shows "Waiting on acceptance" (or leaves the list, for a
  // redirect) at once. If the server refuses (e.g. the email can't be sent) the row goes back and the
  // modal reopens with the pick and note still filled in.
  async function sendReject() {
    if (!rejectRow || !rejectTo) return;
    const row = rejectRow;
    const to = rejectTo;
    const note = rejectNote;
    const mode = rejectMode;
    // Redirect = I'm the PENDING RECIPIENT sending it onward to someone else
    // (not the owner rejecting my own job). The job leaves my queue entirely.
    const isRedirect = !!row.pendingForMe;
    closeReject();
    const done = await optimistic(row, {
      body: {
        action: isRedirect ? 'redirectHandoff' : 'reject',
        jobIdx: row.jobIdx, targetEmail: to, note: note.trim(),
      },
      // Handed off → row goes red "Waiting on acceptance"; Accept/Reject drop away.
      // Redirected → no longer pending for me — it's someone else's now.
      patch: isRedirect ? null : (r => ({ ...r, handoffPending: true, handoffTo: to })),
      hide: isRedirect,
      ok: isRedirect ? 'Sent — no longer on your list.' : 'Handed off — waiting on acceptance.',
      fail: "Couldn't send",
      demoMsg: isRedirect ? 'Sent to someone else. (demo)' : 'Handed off — waiting on acceptance. (demo)',
    });
    if (!done && !demo) {
      setRejectRow(row); setRejectMode(mode); setRejectTo(to); setRejectNote(note);
    }
  }

  // During the tour, render the sample data unconditionally — never gate on the
  // hook's async load/error/empty states (they'd flash the grid empty).
  if (!demo && initialLoading) return <div className="rt-loading">Loading BPPR items…</div>;
  if (!demo && error && !data)  return <div className="rt-error">Error: {error}</div>;

  // Tour: prefer the hook's seeded demo data (reflects optimistic clicks) but
  // fall back to the stable seed until it lands. Normal: the live feed.
  const src = demo ? (data && data.source === 'demo' ? data : demoSeed) : data;
  if (!src) return null;

  const rows = src.rows || [];
  const adminView = !!src.adminView;
  // Split on isDone (see the helper): signed off — a reviewed ASSIGNED row is
  // already accepted-and-signed server-side — closed & won't roll, or an
  // acknowledged unassigned job. Same definition the Home card counts as done.
  const activeRows    = rows.filter(r => !isDone(r));
  const completedRows = rows.filter(isDone);
  const visibleRows   = sortDoneLast(tab === 'completed' ? completedRows : activeRows, isDone);

  // Summary reflects the user's OWN queue — exclude any firm-wide jobs an admin
  // pulled in via the closed / in-progress toggles (r.mine === false) so the
  // counts don't inflate.
  const myRows = rows.filter(r => r.mine !== false);
  const assignedCount   = myRows.filter(r => r.assigned).length;
  const unassignedCount = myRows.filter(r => !r.assigned).length;
  // Jobs I handed off that haven't been accepted yet. They stay visible (so I can
  // pull them back) but they're the recipient's incomplete item, not mine — so
  // they're out of "Items Left", matching the Home card and Steering.
  const awaitingOther   = myRows.filter(r => !isDone(r) && r.handoffPending && !r.pendingForMe).length;
  const completedCount  = myRows.filter(isDone).length;
  const itemsLeft        = myRows.length - completedCount - awaitingOther;

  // Spotlight-tour anchors: tag the first row that shows each affordance so the
  // walkthrough always has a live element to point at (harmless in normal use).
  // Anchored to the ACTIVE rows — the default tab, and the only one guaranteed to
  // be on screen when a tour step fires.
  const byIdx = (pred) => (activeRows.find(pred) || {}).jobIdx;
  const anchorManagers = activeRows[0] ? activeRows[0].jobIdx : null;
  const anchorActions  = byIdx(r => r.assigned && !r.accepted && !r.handoffPending && !r.closed && !r.pendingForMe);
  const anchorReviewed = byIdx(r => r.assigned && r.accepted && !r.closed);
  const anchorQuest    = byIdx(r => r.questionnaireUrl);
  const anchorPending  = byIdx(r => r.pendingForMe);
  const anchorUnassign = byIdx(r => !r.assigned && !r.closed);
  const managerOptions = handoffOptions(rejectRow, email);            // option 1
  const wipOptions     = (rejectRow && rejectRow.wipStaff) || [];     // option 2
  const employees      = src.employees || [];                         // option 3

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">BPPR Review</h2>
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
        {adminView && (
          <span className="bppr-admin-toggles">
            <span className="bppr-closed-toggle" title="Show every closed / reviewed-closed job across the firm so you can undo any manager's close">
              <ToggleSwitch checked={showClosed} onChange={setShowClosed} label="Show closed jobs (firm-wide)" />
            </span>
            <span className="bppr-closed-toggle" title="Show every accepted (in-progress) job across the firm so you can undo any manager's acceptance">
              <ToggleSwitch checked={showInProgress} onChange={setShowInProgress} label="Show in-progress jobs (firm-wide)" />
            </span>
          </span>
        )}
      </div>

      <p className="bppr-sub">
        Business Personal Property Return jobs assigned to you for this period, plus
        unassigned jobs you can acknowledge.
      </p>

      {/* The Practice Engine feed came back empty/unavailable and the backend fell
          back to its built-in sample jobs. Say so loudly: these are fictional
          clients whose actions are ephemeral, and progress here won't match the
          Home card or Steering. (Silence here is what made a stale sample
          population read as "this user has nothing to review.") */}
      {!demo && src.sampleData && (
        <div className="bppr-sample-banner" role="status">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          <span>
            <strong>Sample data.</strong> The Practice Engine BPPR feed is unavailable, so these are
            built-in example jobs — fictional clients, and any action you take here resets on restart.
            Progress shown won&apos;t match Home or Steering. Contact the Data Team if this persists.
          </span>
        </div>
      )}

      <div className="rt-summary" data-tour="bppr-summary">
        <div className="bppr-bigstat">
          <span className="bppr-bigstat-val">{itemsLeft}</span>
          <span className="bppr-bigstat-lbl">Items Left to Review</span>
        </div>
        <div className="rt-stat">
          <span className="rt-stat-val">{completedCount}</span>
          <span className="rt-stat-lbl">Completed</span>
        </div>
        {/* Only when it applies — explains why rows can sit in Active without
            counting toward Items Left. */}
        {awaitingOther > 0 && (
          <div className="rt-stat" title="Jobs you handed off that the recipient hasn't accepted yet. They count in the recipient's queue; use Pull Back to take one over again.">
            <span className="rt-stat-val">{awaitingOther}</span>
            <span className="rt-stat-lbl">Awaiting Others</span>
          </div>
        )}
        <div className="rt-stat">
          <span className="rt-stat-val">{assignedCount}</span>
          <span className="rt-stat-lbl">Assigned to You</span>
        </div>
        <div className="rt-stat">
          <span className="rt-stat-val">{unassignedCount}</span>
          <span className="rt-stat-lbl">Unassigned</span>
        </div>
      </div>

      {/* Active vs Completed. Finished jobs used to be dropped from the payload
          for non-admins, so a manager had no way to see (or correct) what they'd
          signed off — the page just went empty. */}
      <div className="bppr-tabs" role="tablist" aria-label="BPPR queue">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'active'}
          className={`bppr-tab${tab === 'active' ? ' active' : ''}`}
          onClick={() => setTab('active')}
        >
          Active <span className="bppr-tab-count">{activeRows.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'completed'}
          className={`bppr-tab${tab === 'completed' ? ' active' : ''}`}
          onClick={() => setTab('completed')}
        >
          Completed <span className="bppr-tab-count">{completedRows.length}</span>
        </button>
        <span className="bppr-tabs-hint">
          {tab === 'completed'
            ? 'Reviewed, closed, and acknowledged jobs. Undo returns one to Active.'
            : 'Jobs still needing action.'}
        </span>
      </div>

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Client</th>
              <th>Job</th>
              <th>Office</th>
              <th>Managers</th>
              <th>PY Hours</th>
              <th>PY WIP</th>
              <th>PY Billing</th>
              <th>Questionnaire</th>
              <th style={{ textAlign: 'right' }}>
                Action
                <button
                  type="button"
                  className="bppr-help-btn"
                  data-tour="bppr-help"
                  onClick={() => setHelpOpen(true)}
                  title="What do these actions mean?"
                  aria-label="Help"
                >?</button>
              </th>
              <th style={{ textAlign: 'center' }}>Reviewed</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(r => (
              <tr
                key={r.jobIdx}
                className={
                  r.closed ? 'bppr-row-closed'
                    : r.pendingForMe ? 'bppr-row-pending'
                    : r.handoffPending ? 'bppr-row-handoff'
                    : r.reviewed ? 'rt-row-reviewed'
                    : ''
                }
              >
                <td>{r.clientCode}</td>
                <td>
                  {r.clientName}
                  {!r.assigned && <span className="bppr-unassigned-tag">Unassigned</span>}
                </td>
                <td>{r.jobName}</td>
                <td>{r.jobOffice}</td>
                <td className="bppr-mgrs" data-tour={r.jobIdx === anchorManagers ? 'bppr-managers' : undefined}>
                  {MANAGER_ROLES.map(m => {
                    const mgrEmail = r.serviceManagers && r.serviceManagers[m.key];
                    const mgrName  = (r.serviceManagerNames && r.serviceManagerNames[m.key]) || mgrEmail;
                    return (
                      <div key={m.key} className="bppr-person">
                        <span
                          className={`bppr-avatar${mgrName ? '' : ' bppr-avatar--none'}`}
                          title={mgrEmail || `No ${m.label} manager`}
                        >
                          {mgrName ? initials(mgrName) : '—'}
                        </span>
                        <span className="bppr-person-text">
                          <span className="bppr-person-role">{m.label}</span>
                          <span className={`bppr-person-name${mgrName ? '' : ' bppr-person-name--none'}`}>
                            {mgrName || 'N/A'}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </td>
                <td>{r.priorYearHours || '—'}</td>
                <td>{r.priorYearWip || '—'}</td>
                <td>{r.priorYearBilling || '—'}</td>
                <td className="bppr-quest" data-tour={r.jobIdx === anchorQuest ? 'bppr-questionnaire' : undefined}>
                  {r.questionnaireUrl ? (
                    <a href={r.questionnaireUrl} target="_blank" rel="noopener noreferrer" className="bppr-quest-link">
                      SOW&nbsp;↗
                    </a>
                  ) : '—'}
                </td>
                <td
                  className="bppr-actions"
                  data-tour={
                    r.jobIdx === anchorActions ? 'bppr-actions'
                      : r.jobIdx === anchorPending ? 'bppr-pending'
                      : r.jobIdx === anchorUnassign ? 'bppr-acknowledge'
                      : undefined
                  }
                >
                  {isDone(r) ? (
                    // Finished: show WHICH outcome(s) landed — reviewed (still
                    // rolls), closed & won't roll, acknowledged — and offer the
                    // matching undo per outcome. Owners can reverse their own;
                    // admins can reverse anyone's.
                    <>
                      {outcomeTags(r).map(t => (
                        <span key={t.key} className={`bppr-otag ${t.cls}`} title={t.title}>{t.label}</span>
                      ))}
                      {!r.mine && r.assignedManagerName && (
                        <span className="bppr-owner-note" title={r.assignedManagerEmail || undefined}>{r.assignedManagerName}</span>
                      )}
                      {(adminView || r.mine) && (
                        <span className="bppr-undo-row">
                          {r.reviewed && (
                            <button type="button" className="bppr-btn bppr-btn--cancel" onClick={() => unmarkReviewed(r)}>
                              {r.assigned ? 'Undo Review' : 'Undo Acknowledge'}
                            </button>
                          )}
                          {r.closed && (
                            <button type="button" className="bppr-btn bppr-btn--cancel" onClick={() => undoClose(r)}>
                              Undo Close
                            </button>
                          )}
                        </span>
                      )}
                    </>
                  ) : r.pendingForMe ? (
                    <>
                      <span className="bppr-pending-tag">Pending your acceptance</span>
                      <button type="button" className="bppr-btn bppr-btn--accept" onClick={() => acceptHandoff(r)}>Accept</button>
                      <button type="button" className="bppr-btn bppr-btn--reject" onClick={() => declineHandoff(r)}>Decline</button>
                      <button type="button" className="bppr-btn bppr-btn--reject" onClick={() => openReject(r)}>Reassign</button>
                    </>
                  ) : r.assigned ? (
                    r.handoffPending ? (
                      // Sender's view: name the person it's sitting with, and let
                      // the owner take it back. It counts in THEIR queue, not this
                      // one, until they accept, decline, or it's pulled back.
                      <>
                        <span
                          className="bppr-waiting-tag"
                          title={byLine('Waiting on this person to accept. It sits in their queue until they do.',
                                        r.handoffToEmail, r.handoffAt)}
                        >
                          Waiting on {r.handoffTo || 'acceptance'}
                        </span>
                        {(adminView || r.mine) && (
                          <button
                            type="button"
                            className="bppr-btn bppr-btn--cancel"
                            onClick={() => pullBack(r)}
                            title={`Take this job back from ${r.handoffTo || 'them'} — they aren't notified`}
                          >
                            Pull Back
                          </button>
                        )}
                      </>
                    ) : r.accepted ? (
                      r.mine ? (
                        <>
                          <span className="bppr-accepted-tag">Accepted ✓</span>
                          <button type="button" className="bppr-btn bppr-btn--reject" onClick={() => openClose(r)}>
                            Close &amp; Don&apos;t Roll
                          </button>
                        </>
                      ) : (
                        // Firm-wide in-progress row (admin oversight): undo the acceptance.
                        <>
                          <span className="bppr-accepted-tag">Accepted ✓</span>
                          {r.assignedManagerName && (
                            <span className="bppr-owner-note" title={r.assignedManagerEmail || undefined}>{r.assignedManagerName}</span>
                          )}
                          {adminView && (
                            <button type="button" className="bppr-btn bppr-btn--cancel" onClick={() => unaccept(r)}>
                              Undo
                            </button>
                          )}
                        </>
                      )
                    ) : (
                      <>
                        <button type="button" className="bppr-btn bppr-btn--accept" onClick={() => accept(r)}>Accept</button>
                        <button type="button" className="bppr-btn bppr-btn--reject" onClick={() => openReject(r)}>Reject</button>
                        <button type="button" className="bppr-btn bppr-btn--reject" onClick={() => openClose(r)}>Close &amp; Don&apos;t Roll</button>
                      </>
                    )
                  ) : (
                    // Unassigned and not yet acknowledged (acknowledged ones are
                    // finished, so they render above with an Acknowledged chip).
                    <button type="button" className="bppr-btn bppr-btn--accept" onClick={() => acknowledge(r)}>Acknowledge</button>
                  )}
                </td>
                <td className="bppr-reviewed-cell" data-tour={r.jobIdx === anchorReviewed ? 'bppr-reviewed' : undefined}>
                  {r.assigned && !r.pendingForMe && !r.closed ? (
                    r.reviewed ? (
                      // Reviewed = done/closed; Undo lives in the action cell.
                      <span className="bppr-reviewed-check" title="Reviewed &amp; closed">✓</span>
                    ) : (
                      <ToggleSwitch
                        checked={false}
                        disabled={!r.accepted}
                        onChange={checked => toggleReviewed(r, checked)}
                      />
                    )
                  ) : (
                    <span className="rt-cell-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visibleRows.length === 0 && (
          <div className="rt-empty">
            {tab === 'completed'
              ? "Nothing completed yet for this period. Reviewed, closed, and acknowledged jobs land here."
              : rows.length === 0
                ? 'No BPPR items assigned to you or awaiting acknowledgement for this period.'
                : "You're all caught up — every BPPR job is done. See the Completed tab."}
          </div>
        )}
      </div>

      <Modal
        open={!!rejectRow}
        onClose={closeReject}
        title={rejectRow && rejectRow.pendingForMe ? 'Reassign — send to someone else' : 'Reject — reassign this job'}
        footer={
          <>
            <button type="button" className="bppr-btn bppr-btn--cancel" onClick={closeReject}>
              Cancel
            </button>
            <button type="button" className="bppr-btn bppr-btn--accept" onClick={sendReject} disabled={!rejectTo}>
              Send email
            </button>
          </>
        }
      >
        {rejectRow && (
          <>
            <p className="bppr-modal-lead">
              Reassign the BPPR job for <strong>{rejectRow.clientName}</strong> — choose who to send it to.
            </p>

            <div className="bppr-mode-tabs">
              <button type="button" className={`bppr-mode-tab${rejectMode === 'managers' ? ' active' : ''}`} onClick={() => selectMode('managers')}>
                Client&apos;s managers
              </button>
              <button type="button" className={`bppr-mode-tab${rejectMode === 'wip' ? ' active' : ''}`} onClick={() => selectMode('wip')}>
                Had WIP
              </button>
              <button type="button" className={`bppr-mode-tab${rejectMode === 'any' ? ' active' : ''}`} onClick={() => selectMode('any')}>
                Any employee
              </button>
            </div>

            {rejectMode === 'managers' && (
              managerOptions.length === 0 ? (
                <p className="rt-empty">No other CAS/Tax/Audit managers are set for this client.</p>
              ) : (
                <div className="bppr-option-list">
                  {managerOptions.map(o => (
                    <label key={o.key} className="bppr-option">
                      <input type="radio" name="bppr-reject-target" value={o.email} checked={rejectTo === o.email} onChange={() => setRejectTo(o.email)} />
                      <span className="bppr-option-role">{o.label}</span>
                      <span className="bppr-option-email" title={o.email}>{o.name}</span>
                    </label>
                  ))}
                </div>
              )
            )}

            {rejectMode === 'wip' && (
              wipOptions.length === 0 ? (
                <p className="rt-empty">No one has logged recent WIP on this client.</p>
              ) : (
                <div className="bppr-option-list">
                  {wipOptions.map(o => (
                    <label key={o.email} className="bppr-option">
                      <input type="radio" name="bppr-reject-target" value={o.email} checked={rejectTo === o.email} onChange={() => setRejectTo(o.email)} />
                      <span className="bppr-option-email" title={o.email}>{o.name}</span>
                    </label>
                  ))}
                </div>
              )
            )}

            {rejectMode === 'any' && (
              <select className="bppr-any-select" value={rejectTo} onChange={e => setRejectTo(e.target.value)}>
                <option value="">— Select an employee —</option>
                {employees.map(o => (
                  <option key={o.email} value={o.email}>{o.name}</option>
                ))}
              </select>
            )}

            <label className="bppr-note-label">
              Note (optional) — included in the email
              <textarea
                className="bppr-note-input"
                rows={3}
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                placeholder="Add a note for the recipient…"
               
              />
            </label>
          </>
        )}
      </Modal>

      <Modal
        open={!!closeRow}
        onClose={cancelClose}
        title="Close & Don't Roll"
        footer={
          <>
            <button type="button" className="bppr-btn bppr-btn--cancel" onClick={cancelClose}>
              Cancel
            </button>
            <button type="button" className="bppr-btn bppr-btn--reject" onClick={confirmClose}>
              {"Close & Don't Roll"}
            </button>
          </>
        }
      >
        {closeRow && (
          <p className="bppr-modal-lead">
            Close the BPPR job for <strong>{closeRow.clientName}</strong> ({closeRow.jobName}) and
            flag it <strong>not to roll forward</strong> next year? This is recorded now and applied
            in Practice Engine later.
          </p>
        )}
      </Modal>

      <Modal open={helpOpen} onClose={() => setHelpOpen(false)} title="BPPR Review — actions">
        <dl className="bppr-help-dl">
          <dt>Active / Completed tabs</dt>
          <dd><strong>Active</strong> is everything still needing action. <strong>Completed</strong> holds your finished jobs, and its count matches the BPPR figure on your dashboard. Finishing a job moves it from one tab to the other; the <strong>Undo</strong> on a completed row brings it back to Active.</dd>
          <dt>Completed badges</dt>
          <dd>
            A finished job is tagged with what actually happened, and can carry two:
            <br /><span className="bppr-otag bppr-otag--reviewed">Reviewed</span> you accepted it and signed off — it <strong>still rolls forward</strong> next year.
            <br /><span className="bppr-otag bppr-otag--noroll">Closed — Won&apos;t Roll</span> flagged <strong>not</strong> to roll forward next year. Applied in Practice Engine later.
            <br /><span className="bppr-otag bppr-otag--ack">Acknowledged</span> an unassigned job you accounted for <strong>without</strong> taking ownership.
            <br />Hover any badge to see who did it and when.
          </dd>
          <dt>Accept</dt>
          <dd>For a job <strong>assigned to you</strong>. Takes ownership — Accept/Reject drop away (Close &amp; Don't Roll stays), and the <strong>Reviewed</strong> toggle becomes available. It does <strong>not</strong> mark the job reviewed on its own.</dd>
          <dt>Reject</dt>
          <dd>Hand the job to someone else — one of the client's CAS / Tax / Audit managers, anyone who logged WIP, or any employee — who gets an email. Your row then reads <strong>&ldquo;Waiting on &lt;name&gt;&rdquo;</strong> until they act. While it&apos;s waiting it counts as <strong>their</strong> outstanding item, not yours, on both your dashboard and Steering — so it drops out of your <strong>Items Left</strong> and shows under <strong>Awaiting Others</strong>.</dd>
          <dt>Pull Back</dt>
          <dd>On a job you handed off that hasn&apos;t been accepted yet: takes it back into your own queue, actionable again and back in your counts. The recipient is <strong>not</strong> emailed — they hadn&apos;t taken it on. Hover &ldquo;Waiting on&hellip;&rdquo; to see who has it and when it was sent.</dd>
          <dt>Pending your acceptance</dt>
          <dd>A job someone handed to <strong>you</strong>. <strong>Accept</strong> takes ownership; <strong>Decline</strong> sends it back to the person who handed it to you; <strong>Reassign</strong> sends it to someone else entirely (same picker as Reject) — if they later decline, it returns to the original owner, not to you.</dd>
          <dt>Acknowledge</dt>
          <dd>For a job that <strong>isn't assigned to you</strong>. Marks it reviewed so it's accounted for, without taking ownership.</dd>
          <dt>Mark Reviewed (toggle)</dt>
          <dd>Enabled once you've <strong>accepted</strong> the job. Toggle on to sign off — this <strong>also closes the item</strong> and drops it off your list. The job <strong>still rolls forward</strong> next year; use <strong>Close &amp; Don't Roll</strong> instead when it shouldn't.</dd>
          <dt>Close &amp; Don't Roll</dt>
          <dd>Standalone — available on any job assigned to you (alongside Accept/Reject, or after accepting; no review needed). Emails you a confirmation and — only after it sends — closes the job so it won't roll next year and removes it from your list. Recorded now; applied to Practice Engine later.</dd>
        </dl>
      </Modal>
    </div>
  );
}
